<template>
  <div id="app">
    <div>
      <NetworkStatus />
    </div>
    <AppAnnouncementsHost
      :announcements="visibleAnnouncements"
      mode="top"
      @click="handleAnnouncementClick"
      @dismiss="handleAnnouncementDismiss"
      @acknowledge="handleAnnouncementAcknowledge"
    />
    <AppAnnouncementsHost
      :announcements="visibleAnnouncements"
      mode="modal"
      @click="handleAnnouncementClick"
      @dismiss="handleAnnouncementDismiss"
      @acknowledge="handleAnnouncementAcknowledge"
    />
    <UserSecurityChecker />
    <AdminSupportStaffModalRoot />
    <Loader />
    <router-view/>
    <HardwareServiceChecker/>
  </div>
</template>
<script>
import Loader from "@/components/layout/Loader";
import UserSecurityChecker from "@/components/UserSecurityChecker.vue";
import NetworkStatus from "@/components/layout/NetworkStatus.vue";
import store from "@/store";
import HardwareServiceChecker from "@/components/layout/HardwareServiceChecker.vue";
import AppAnnouncementsHost from "@/components/layout/announcements/AppAnnouncementsHost.vue";
import { announcementsService } from "@/services/announcements.service";
import { isUpdateApiAvailable, init as initUpdateService, checkForUpdate } from "@/services/updateService";
import connectivityMonitor from "@/services/connectivityMonitor";
import AdminSupportStaffModalRoot from "@/components/security/AdminSupportStaffModalRoot.vue";

const ANNOUNCEMENT_DISMISSALS_KEY = "lytAnnouncementsDismissals";
const ANNOUNCEMENT_ACKNOWLEDGED_KEY = "lytAnnouncementsAcknowledged";

export default {
  components: {NetworkStatus, UserSecurityChecker,HardwareServiceChecker,Loader,AppAnnouncementsHost,AdminSupportStaffModalRoot},
  async beforeMount() {
    // Ticket #26668: pole/customer display is a thin second-screen client.
    // Skip sync service, shortcuts, and full data sync so the second renderer
    // does not boot the entire POS shell (which crashed under loadFile race).
    const routeName = (this.$route && this.$route.name) || this.name;
    const routePath = (this.$route && this.$route.path) || '';
    if (routeName === 'customer-display' || routePath === '/customer-display') {
      return;
    }
    if (!this.$store.getters.isHydrated) {
      await new Promise((resolve) => {
        const interval = setInterval(() => {
          if (this.$store.getters.isHydrated) {
            clearInterval(interval);
            resolve();
          }
        }, 50);  // Check every 50ms
      });
    }
    this.$store.dispatch('startSyncService');
    await this.$store.dispatch('loadShortcuts');
    if (!routeExceptions.includes(this.name)) {
      try {
        await this.$store.dispatch('syncData');
      } catch (err) {
        // Ignore benign router navigation interrupts during startup
        const msg = (err && err.message) || '';
        const name = (err && err.name) || '';
        if (!/NavigationCancelled|NavigationAborted|NavigationDuplicated/i.test(name + ' ' + msg)) {
          // Log unexpected errors only
          console.warn('[App.beforeMount] syncData failed:', err);
        }
      }
    }
  },
  computed : {
    loading(){
      return this.$store.getters.getLoadingState;
    },
    visibleAnnouncements() {
      return this.announcements.filter((announcement) => {
        if (!announcement || !announcement.id) return false;
        if (this.dismissedAnnouncements?.[announcement.id]) return false;
        if (announcement.requiresAcknowledgement && this.acknowledgedAnnouncements?.[announcement.id]) return false;
        return true;
      });
    },
  },
  data() {
    return {
      name: this.$route.name,
      resizeListener: (e) => this.$store.commit('setWindowWidth'),
      announcements: [],
      announcementContext: null,
      dismissedAnnouncements: {},
      acknowledgedAnnouncements: {},
    }
  },
  created() {
    // Ticket #26668: customer-display only needs theme + route render.
    const routeName = (this.$route && this.$route.name) || this.name;
    const routePath = (this.$route && this.$route.path) || '';
    if (routeName === 'customer-display' || routePath === '/customer-display') {
      this.$store.dispatch('initializeTheme');
      return;
    }
    this.$store.dispatch('initializeTheme');
    window.addEventListener('resize', this.resizeListener);
    this.loadDismissedAnnouncements();
    this.loadAcknowledgedAnnouncements();
  },
  mounted() {
    // Ticket #26668: no announcements / updater on pole display thin client.
    const routeName = (this.$route && this.$route.name) || this.name;
    const routePath = (this.$route && this.$route.path) || '';
    if (routeName === 'customer-display' || routePath === '/customer-display') {
      return;
    }
    this.fetchAnnouncements();

    // Wire the in-app update flow. See docs/exec-plans/active/2026-06-23-app-update-flow.md.
    // Skip entirely when the preload bridge isn't available (e.g. web
    // build) so we don't blow up in the browser.
    if (isUpdateApiAvailable()) {
      try {
        this._unsubscribeUpdates = initUpdateService(this.$store);
      } catch (e) {
        console.warn('[App] Failed to wire update service:', e && e.message);
      }
      // Subscribe to connectivity events so we re-check when the
      // device comes back online. The 6h periodic check in main is
      // independent of this; this is just a faster re-check on
      // reconnect.
      this._unsubscribeConnectivity = connectivityMonitor.subscribe && connectivityMonitor.subscribe('online', () => {
        checkForUpdate().catch(() => { /* logged inside */ });
      });
    }
  },
  methods: {
    loadDismissedAnnouncements() {
      try {
        const raw = localStorage.getItem(ANNOUNCEMENT_DISMISSALS_KEY);
        this.dismissedAnnouncements = raw ? JSON.parse(raw) : {};
      } catch (_) {
        this.dismissedAnnouncements = {};
      }
    },
    persistDismissedAnnouncements() {
      try {
        localStorage.setItem(ANNOUNCEMENT_DISMISSALS_KEY, JSON.stringify(this.dismissedAnnouncements));
      } catch (_) {}
    },
    loadAcknowledgedAnnouncements() {
      try {
        const raw = localStorage.getItem(ANNOUNCEMENT_ACKNOWLEDGED_KEY);
        this.acknowledgedAnnouncements = raw ? JSON.parse(raw) : {};
      } catch (_) {
        this.acknowledgedAnnouncements = {};
      }
    },
    persistAcknowledgedAnnouncements() {
      try {
        localStorage.setItem(ANNOUNCEMENT_ACKNOWLEDGED_KEY, JSON.stringify(this.acknowledgedAnnouncements));
      } catch (_) {}
    },
    async fetchAnnouncements() {
      try {
        this.announcementContext = await announcementsService.resolveContext();
        this.announcements = await announcementsService.fetchVisibleAnnouncements(this.announcementContext);
      } catch (error) {
        console.warn("[Announcements] Failed to load announcements", error);
        this.announcements = [];
      }
    },
    async buildAnnouncementInteractionPayload() {
      if (this.announcementContext) {
        return this.announcementContext;
      }

      this.announcementContext = await announcementsService.resolveContext();
      return this.announcementContext;
    },
    async handleAnnouncementDismiss(announcement) {
      if (!announcement?.id) return;

      this.$set(this.dismissedAnnouncements, announcement.id, true);
      this.persistDismissedAnnouncements();

      try {
        const payload = await this.buildAnnouncementInteractionPayload();
        await announcementsService.dismissAnnouncement(announcement.id, payload);
      } catch (error) {
        console.warn("[Announcements] Failed to record dismissal", error);
      }
    },
    async handleAnnouncementAcknowledge(announcement) {
      if (!announcement?.id) return;

      this.$set(this.acknowledgedAnnouncements, announcement.id, true);
      this.persistAcknowledgedAnnouncements();

      if (announcement.dismissible !== false) {
        this.$set(this.dismissedAnnouncements, announcement.id, true);
        this.persistDismissedAnnouncements();
      }

      try {
        const payload = await this.buildAnnouncementInteractionPayload();
        await announcementsService.acknowledgeAnnouncement(announcement.id, payload);
      } catch (error) {
        console.warn("[Announcements] Failed to record acknowledgement", error);
      }
    },
    async handleAnnouncementClick(announcement) {
      if (!announcement) return;

      try {
        const payload = await this.buildAnnouncementInteractionPayload();
        announcementsService.trackAnnouncementClick(announcement.id, payload).catch((error) => {
          console.warn("[Announcements] Failed to track click", error);
        });
      } catch (error) {
        console.warn("[Announcements] Failed to build click payload", error);
      }

      if (!announcement.link) return;

      const linkType = (announcement.linkType || "").toLowerCase();
      if ((linkType === "internal" || linkType === "route") && this.$router) {
        this.$router.push(announcement.link).catch(() => {});
        return;
      }

      if (linkType === "modal") {
        this.$utils?.showInfo?.(announcement.message || announcement.title || "Announcement");
        return;
      }

      if (typeof window.openUrl === "function") {
        window.openUrl(announcement.link);
        return;
      }

      window.open(announcement.link, "_blank");
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeListener)
    if (this._unsubscribeUpdates) {
      try { this._unsubscribeUpdates(); } catch (_) { /* ignore */ }
      this._unsubscribeUpdates = null;
    }
    if (this._unsubscribeConnectivity) {
      try { this._unsubscribeConnectivity(); } catch (_) { /* ignore */ }
      this._unsubscribeConnectivity = null;
    }
  }
}
</script>
<style lang="scss">
@import "assets/css/styles";
</style>
